import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import AdminPageNavigator from "../components/AdminPageNavigator";
import { bookLanguageDisplay } from "../i18n/bookLanguageDisplay";
import { useTranslation } from "../i18n/useTranslation";
import { normalizeFrontendStoryStyleId, type StoryStyleId } from "../constants/storyStyles";
import { API_BASE_URL } from "../config/apiBaseUrl";

type BookRequestSummary = {
  sessionId: string;
  language: string;
  storyType: string;
  storyStyle?: StoryStyleId;
  storyDescription: string;
  storyGuidelines: string;
  imageCount: number;
  images: Array<{ originalName?: string; tags?: string }>;
  submissionStatus?: string;
  userChangeRequestText?: string;
  userChangeRequestedAt?: string | null;
  createdAt: string;
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  "Story Submitted": "orderSummary.statusStorySubmitted",
  "Story In Progress": "orderSummary.statusStoryInProgress",
  "Finising Touches": "orderSummary.statusFinishingTouches",
  "Your Story Is Ready": "orderSummary.statusReady",
  "Required Changes Submitted": "orderSummary.statusRequiredChangesSubmitted",
};

const BookRequestSubmitted: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [request, setRequest] = useState<BookRequestSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem("mysipur_auth_token");
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchRequest = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/stories/book-request`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.success && data?.data) {
          const row = data.data;
          setRequest({
            ...row,
            storyStyle: normalizeFrontendStoryStyleId(row.storyStyle),
          });
        } else {
          setRequest(null);
        }
      } catch {
        setRequest(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequest();
  }, [navigate]);

  useEffect(() => {
    if (!isLoading && request === null) {
      navigate("/create/language", { replace: true });
    }
  }, [isLoading, request, navigate]);

  const storyTypeLabel = useMemo(() => {
    if (!request) return "—";
    if (request.storyType === "surrogacy") return t("orderSummary.storyTypeSurrogacy");
    if (request.storyType === "general") return t("orderSummary.storyTypeGeneral");
    return request.storyType || "—";
  }, [request, t]);

  const illustrationStyleLabel = useMemo(() => {
    if (!request?.storyStyle) return "—";
    return t(`wizard.storyStyle.${request.storyStyle}Title`);
  }, [request, t]);

  if (isLoading) {
    return (
      <div className="relative min-h-screen">
        <div className="pointer-events-none fixed inset-0 z-0 book-request-submitted-bg" aria-hidden />
        <div className="pointer-events-none fixed inset-0 z-[1] bg-brandBeige/40" aria-hidden />
        <div className="relative z-10 flex min-h-screen flex-col">
          <main
            id="main-content"
            tabIndex={-1}
            className="flex flex-1 items-center justify-center px-4 outline-none"
            aria-busy="true"
          >
            <p className="text-lg">{t("orderSummary.loading")}</p>
          </main>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const rawStatus = request.submissionStatus || "Story Submitted";
  const statusLabel = STATUS_LABEL_KEYS[rawStatus] ? t(STATUS_LABEL_KEYS[rawStatus]) : rawStatus;
  const statusStyles = (() => {
    switch (rawStatus) {
      case "Story Submitted":
        return { bg: "bg-blue-50", text: "text-blue-800", ring: "ring-blue-200" };
      case "Story In Progress":
        return { bg: "bg-amber-50", text: "text-amber-800", ring: "ring-amber-200" };
      case "Finising Touches":
        return { bg: "bg-purple-50", text: "text-purple-800", ring: "ring-purple-200" };
      case "Your Story Is Ready":
        return { bg: "bg-green-50", text: "text-green-800", ring: "ring-green-200" };
      case "Required Changes Submitted":
        return { bg: "bg-red-50", text: "text-red-800", ring: "ring-red-200" };
      default:
        return { bg: "bg-gray-50", text: "text-gray-800", ring: "ring-gray-200" };
    }
  })();
  const canSeeDigitalBook = rawStatus === "Your Story Is Ready" || rawStatus === "Required Changes Submitted";

  return (
    <div className="relative min-h-screen">
      <div className="pointer-events-none fixed inset-0 z-0 book-request-submitted-bg" aria-hidden />
      <div className="pointer-events-none fixed inset-0 z-[1] bg-brandBeige/40" aria-hidden />

      <div className="relative z-10 flex min-h-screen flex-col">
        <main id="main-content" tabIndex={-1} className="max-w-2xl mx-auto px-4 py-12 w-full outline-none flex-1">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-card border border-white/40 p-8">
            <h1 className="text-3xl font-extrabold text-heading mb-2 text-center">
              {t("orderSummary.title")}
            </h1>
            <AdminPageNavigator align="center" />
            <p className="text-center text-body mb-8">{t("orderSummary.intro")}</p>

            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-heading">{t("orderSummary.requestSummary")}</h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <strong>{t("orderSummary.status")}</strong>
                  <span
                    className={`${statusStyles.bg} ${statusStyles.text} ring-1 ${statusStyles.ring} font-extrabold px-3 py-1.5 rounded-full text-sm`}
                  >
                    {statusLabel}
                  </span>
                </div>
                {rawStatus === "Required Changes Submitted" &&
                  request.userChangeRequestText &&
                  request.userChangeRequestText.trim().length > 0 && (
                  <div>
                    <strong>{t("orderSummary.submittedChangeRequest")}</strong>
                    <p className="mt-1 text-body whitespace-pre-wrap">{request.userChangeRequestText}</p>
                  </div>
                )}
                <p>
                  <strong>{t("orderSummary.language")}</strong>{" "}
                  {bookLanguageDisplay(request.language, t)}
                </p>
                <p>
                  <strong>{t("orderSummary.storyType")}</strong> {storyTypeLabel}
                </p>
                <p>
                  <strong>{t("orderSummary.illustrationStyle")}</strong> {illustrationStyleLabel}
                </p>
                <p>
                  <strong>{t("orderSummary.images")}</strong> {request.imageCount}{" "}
                  {t("orderSummary.uploadedSuffix")}
                </p>
                {request.images?.length > 0 && (
                <div>
                  <strong>{t("orderSummary.peopleInPhotos")}</strong>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    {request.images.map((img, i) => (
                      <li key={i}>
                        {img.originalName || `Image ${i + 1}`}
                        {img.tags ? ` – ${img.tags}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
                <div>
                  <strong>{t("orderSummary.storyDescription")}</strong>
                  <p className="mt-1 text-body whitespace-pre-wrap">{request.storyDescription}</p>
                </div>
                {request.storyGuidelines && (
                <div>
                  <strong>{t("orderSummary.guidelines")}</strong>
                  <p className="mt-1 text-body whitespace-pre-wrap">{request.storyGuidelines}</p>
                </div>
              )}
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <div className="flex flex-wrap justify-center gap-3">
                {canSeeDigitalBook && (
                  <button
                    type="button"
                    onClick={() => navigate(`/create/result?sessionId=${encodeURIComponent(request.sessionId)}`)}
                    className="bg-brandOrange text-white font-semibold py-3 px-8 rounded-2xl shadow-soft hover:shadow-card transition-all"
                  >
                    {t("orderSummary.seeDigitalBook")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="bg-brandNavy text-white font-semibold py-3 px-8 rounded-2xl shadow-soft hover:shadow-card transition-all"
                >
                  {t("orderSummary.backHome")}
                </button>
              </div>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    </div>
  );
};

export default BookRequestSubmitted;
